import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';

import {
	StyledThumbnailsItem,
	ThumbnailsInner,
	ThumbnailsItemPosition,
	ThumbnailsImageWrapper,
	ThumbnailsInfo,
	ThumbnailsItemLabel,
	ThumbnailsEditIcon,
	ThumbnailsSelectedNumber,
} from '../styles';
import FileIcon from '../../FileIcon';
import { Api } from '../../../../typings/api';
import { changePage, selectPage, setPageSelection, resetPageSelection } from '../../../../containers/Editor/actions';
import { StoreState } from '../../../../reducers';
import Utils from '../../../../utils';
import UserAgentUtils from '../../../../utils/userAgent';

interface ThumbnailsItemProps {
	position: number;
	label: string;
	type: Api.AssetItemFileTypes;
	fileFormat: Api.AssetItemFileFormats;
	imageSrc: string;
	isProposal: boolean;
	isActive: boolean;
	isEditable: boolean;
	isPageSelectionActive: boolean;
	activeDraggingPosition: number | null;
	isAllPagesSelected: boolean;
	isGapInSelectedPages: boolean;
	isPageSelectionAvailable: boolean;
	selectedPagesNumber?: number;
	dispatch: Dispatch<StoreState>;
}

const ThumbnailsItem = ({
	position,
	label,
	type,
	fileFormat,
	imageSrc,
	isProposal,
	isActive,
	isEditable,
	isPageSelectionActive,
	activeDraggingPosition,
	isAllPagesSelected,
	isGapInSelectedPages,
	isPageSelectionAvailable,
	selectedPagesNumber,
	dispatch,
}: ThumbnailsItemProps) => {
	const button = React.useRef<HTMLButtonElement>(null);

	const handleClick = (e: MouseEvent | TouchEvent) => {
		if (UserAgentUtils.isTouchSupported()) {
			if (isPageSelectionActive) {
				dispatch(selectPage(position));
			} else if (!isActive) {
				dispatch(changePage(position));
			}
		} else {
			const isPageSelectionMode = isPageSelectionAvailable && (e.ctrlKey || e.metaKey || e.shiftKey);
			if (isPageSelectionMode) {
				if (!isPageSelectionActive) {
					dispatch(setPageSelection());
				}
				dispatch(selectPage(position, e.ctrlKey || e.metaKey, e.shiftKey));
			} else {
				if (isPageSelectionActive) {
					dispatch(resetPageSelection());
					dispatch(changePage(position));
				} else if (!isActive) {
					dispatch(changePage(position));
				}
			}
		}
	};

	React.useEffect(() => {
		if (isActive && button.current) {
			button.current.focus();
		}
	}, [isActive]);

	const isDragDisabled = isPageSelectionActive && (
		!isActive ||
		isGapInSelectedPages ||
		isAllPagesSelected
	);
	const isBlurred = isPageSelectionActive &&
		isActive &&
		activeDraggingPosition &&
		position !== activeDraggingPosition;

	return (
		<Draggable
			draggableId={position.toString()}
			index={position}
			isDragDisabled={isDragDisabled}
		>
			{(provided: DraggableProvided, { isDragging }: DraggableStateSnapshot) => (
				<StyledThumbnailsItem
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					isActive={isActive}
					isBlurred={isBlurred}
				>
					<ThumbnailsInner
						ref={button}
						tabIndex={0}
						onClick={handleClick}
					>
						<ThumbnailsItemPosition>{position}</ThumbnailsItemPosition>
						<ThumbnailsImageWrapper>
							<img
								className="lazyload"
								data-src={imageSrc}
								alt=""
							/>
						</ThumbnailsImageWrapper>
						<ThumbnailsInfo>
							<FileIcon
								type={type}
								fileFormat={fileFormat}
								isProposal={isProposal}
								isActive={isActive}
							/>
							<ThumbnailsItemLabel
								isActive={isActive}
							>
								{label}
							</ThumbnailsItemLabel>
						</ThumbnailsInfo>
						{isEditable &&
							<ThumbnailsEditIcon>
								{Utils.getSvgIcon('iconset_uprez_brush-bold')}
							</ThumbnailsEditIcon>
						}
						{isPageSelectionActive && selectedPagesNumber && selectedPagesNumber > 1 && isDragging &&
							<ThumbnailsSelectedNumber>
								{selectedPagesNumber}
							</ThumbnailsSelectedNumber>
						}
					</ThumbnailsInner>
				</StyledThumbnailsItem>
			)}
		</Draggable>
	);
};

export default React.memo(ThumbnailsItem);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Thumbnails/ThumbnailsItem/index.tsx